import { Box, ListItemText } from "@mui/material";
import {
  Field,
  FieldInputProps,
  FieldMetaProps,
  useFormikContext,
} from "formik";
import { ReactElement, useCallback } from "react";

import {
  GetNgpVanSurveyQuestionQuery,
  GetNgpVanSurveyQuestionQueryVariables,
  GetNgpVanSurveyQuestionsQuery,
  GetNgpVanSurveyQuestionsQueryVariables,
  NgpVanSurveyQuestionFieldsFragment,
  NgpVanDatabaseModeGqlType,
  NgpVanObjectType,
} from "~frontend/__generated__/graphql";
import {
  GET_NGPVAN_SURVEY_QUESTION,
  GET_NGPVAN_SURVEY_QUESTIONS,
} from "~frontend/graphql/integration";
import { palette } from "~frontend/theme";

import NgpVanObjectAutocomplete from "./NgpVanObjectAutocomplete";
import { BaseProps } from "./types";

const AUTO_REFRESH_SURVEY_QUESTIONS = 1000 * 60 * 60 * 24; // 1 day

export function NgpVanSurveyQuestionAutocomplete({
  onChange,
  ...baseProps
}: BaseProps & {
  onChange: (newValue: number | undefined) => void;
}): ReactElement {
  const { integrationId, databaseMode } = baseProps;

  return NgpVanObjectAutocomplete<
    NgpVanSurveyQuestionFieldsFragment,
    GetNgpVanSurveyQuestionQuery,
    GetNgpVanSurveyQuestionQueryVariables,
    GetNgpVanSurveyQuestionsQuery,
    GetNgpVanSurveyQuestionsQueryVariables
  >({
    ...baseProps,
    objectType: NgpVanObjectType.SurveyQuestions,
    autoRefreshWindowMillis: AUTO_REFRESH_SURVEY_QUESTIONS,
    noResultsLabel: "No Survey Questions Found",
    errorLabel: "Error Loading Survey Questions",
    onChange: newSurveyQuestion => {
      onChange(newSurveyQuestion?.surveyQuestionId ?? undefined);
    },
    getLastRefreshTime: useCallback(
      integration => {
        const maybeTimestamp =
          databaseMode === NgpVanDatabaseModeGqlType.MyVoters
            ? integration.ngpVanMyVotersSurveyQuestionLastRefresh
            : integration.ngpVanMyCampaignSurveyQuestionLastRefresh;

        return maybeTimestamp ? new Date(maybeTimestamp) : null;
      },
      [databaseMode]
    ),
    getLastRefreshJob: useCallback(
      integration => {
        return (
          (databaseMode === NgpVanDatabaseModeGqlType.MyVoters
            ? integration.ngpVanMyVotersSurveyQuestionRefreshJob
            : integration.ngpVanMyCampaignSurveyQuestionRefreshJob) ?? null
        );
      },
      [databaseMode]
    ),

    getObjectQuery: GET_NGPVAN_SURVEY_QUESTION,
    getObjectQueryVariables: useCallback(
      value => ({
        input: {
          integrationId,
          databaseMode,
          surveyQuestionId: value,
        },
      }),
      [integrationId, databaseMode]
    ),
    getObjectQueryResultToObject: useCallback(
      data => data.getNgpVanSurveyQuestion ?? null,
      []
    ),
    objectToGetObjectQueryResult: useCallback(
      surveyQuestion => ({
        getNgpVanSurveyQuestion: surveyQuestion,
      }),
      []
    ),

    listObjectQuery: GET_NGPVAN_SURVEY_QUESTIONS,
    listObjectQueryVariables: useCallback(
      nameContains => ({
        input: {
          integrationId,
          databaseMode,
          nameContains: nameContains || null,
        },
      }),
      [integrationId, databaseMode]
    ),
    listObjectQueryResultToObjects: useCallback(
      data => data.getNgpVanSurveyQuestions,
      []
    ),

    searchTextFromObject: useCallback(
      surveyQuestion => surveyQuestion.name,
      []
    ),
    valueFromObject: useCallback(
      surveyQuestion => surveyQuestion.surveyQuestionId,
      []
    ),
    listItemFromObject: useCallback(
      surveyQuestion => (
        <ListItemText
          primary={surveyQuestion.name}
          primaryTypographyProps={{ component: "div" }}
          secondary={
            surveyQuestion.scriptQuestion ? (
              <Box sx={{ fontSize: "12px", color: palette.navy }}>
                <i>{surveyQuestion.scriptQuestion}</i>
              </Box>
            ) : undefined
          }
          secondaryTypographyProps={{ component: "div" }}
        />
      ),
      []
    ),
  });
}

export function FormikNgpVanSurveyQuestionAutocomplete({
  label,
  name,
  responseFieldName,
  integrationId,
  databaseMode,
  readOnly,
}: {
  label: string;
  name: string;
  responseFieldName: string;
  integrationId: string;
  databaseMode: NgpVanDatabaseModeGqlType;
  readOnly?: boolean;
}): ReactElement {
  const formik = useFormikContext<any>();

  return (
    <Field name={name}>
      {({
        field,
        meta,
      }: {
        field: FieldInputProps<unknown>;
        meta: FieldMetaProps<unknown>;
      }) => (
        <NgpVanSurveyQuestionAutocomplete
          label={label}
          integrationId={integrationId}
          databaseMode={databaseMode}
          readOnly={readOnly}
          value={field.value as number}
          onChange={async newValue => {
            // last argument is a boolean for whether to re-validate the form--
            // we only do that on the last call
            await formik.setFieldValue(name, newValue, false);
            await formik.setFieldTouched(name, true, false);

            await formik.setFieldValue(responseFieldName, "", false);
            await formik.setFieldTouched(responseFieldName, false, true);
          }}
          error={Boolean(meta.touched && meta.error)}
          helperText={meta.touched ? meta.error : null}
        />
      )}
    </Field>
  );
}
